<template>
  <table class="data-table">
    <thead>
      <tr class="header-row">
        <th v-for="col in visibleColumns" :key="col" class="header-cell">
          <div class="header-content" @click="handleSort(col)">
            <span class="header-text">{{ col }}</span>
            <div class="sort-indicator" v-if="sortConfig.column === col">
              <svg class="sort-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none"
                v-html="getIconPath(sortConfig.direction === 'asc' ? iconNames.arrowUp : iconNames.arrowDown)">
              </svg>
            </div>
          </div>
        </th>
        <th v-if="hasButtonActions" class="header-cell actions-header">
          <div class="header-content">
            <span class="header-text">Actions</span>
          </div>
        </th>
      </tr>
    </thead>

    <tbody>
      <tr
        v-for="(row, index) in sortedAndFilteredRows"
        :key="index"
        class="data-row"
        :class="{
          'row-even': index % 2 === 0,
          'row-odd': index % 2 === 1,
          'row-clickable': rowClickAction(row)
        }"
        @click="handleRowClick(row, $event)"
      >
        <td v-for="col in visibleColumns" :key="col" class="data-cell">
          <div class="cell-content">
            <!-- Toggle column -->
            <button
              v-if="toggleColumns?.[col]"
              :class="['toggle-btn', row[col] ? 'toggle-btn--on' : 'toggle-btn--off']"
              @click.stop="toggleColumns[col](row)"
            >
              <span class="toggle-knob" />
            </button>

            <!-- Editable column input -->
            <input
              v-else-if="editableColumns.includes(col) && editableColumnTypes[col] === 'number' && isRowEditing(row)"
              v-model.number="row[col]"
              type="number"
              class="editable-input"
              @keypress="preventNonNumeric($event)"
              @paste="handlePaste($event)"
              step="any"
              @click.stop
            />

            <!-- Read-only with short/full logic -->
            <template v-else>
              <span v-if="row[col]?.short && row[col]?.full" :title="row[col].full">
                {{ row[col].short }}
              </span>
              <span v-else>
                {{ row[col] }}
              </span>
            </template>

            <span
              v-if="badgeColumns?.[col] && row[badgeColumns[col]]"
              class="row-badge"
              data-testid="row-group-badge"
            >{{ row[badgeColumns[col]] }}</span>
          </div>
        </td>

        <td v-if="buttonActions(row).length > 0" class="data-cell actions-cell">
          <div class="actions-container">
            <button
              v-for="action in buttonActions(row)"
              :key="action.label || action.icon"
              @click="handleAction(action, row, $event, index)"
              :disabled="activeAction[`${index}-${action.label || action.id}`]"
              :class="[
                'action-btn',
                `action-btn-${action.variant || 'default'}`,
                { loading: activeAction[`${index}-${action.label || action.id}`] }
              ]"
            >
              <svg
                v-if="activeAction[`${index}-${action.label || action.id}`]"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                class="spinner-icon"
              >
                <g v-html="getIconPath(iconNames.spinner2)"></g>
              </svg>

              <template v-else>
                <svg v-if="action.icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <g v-html="getIconPath(action.icon)"></g>
                </svg>
                <span v-else class="action-text">{{ action.label }}</span>
              </template>
            </button>
          </div>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script setup>
import { getIconPath, iconNames } from '@/Modules/utils/icons';
import { computed, reactive } from 'vue';

const props = defineProps({
  sortConfig: {
    type: Object,
    required: true,
  },
  sortedAndFilteredRows: {
    type: Array,
    required: true,
  },
  actions: {
    type: [Array, Function],
    default: () => []
  },
  visibleColumns: {
    type: Array,
    required: true
  },
  editableColumns: {
    type: Array,
    default: () => []
  },
  editableColumnTypes: {
    type: Object,
    default: () => ({})
  },
  rowEditableState: {
    type: Object,
    default: () => ({})
  },
  toggleColumns: {
    type: Object,
    default: () => ({})
  },
  badgeColumns: {
    type: Object,
    default: () => ({})
  }
});

const activeAction = reactive({});

const isRowEditing = (row) =>
  !!props.rowEditableState &&
  Object.values(row).some(v => v && props.rowEditableState[v] === true);

const preventNonNumeric = (event) => {
  const char = event.key;
  if (!/[\d.]/.test(char) || (char === '.' && event.target.value.includes('.'))) {
    event.preventDefault();
  }
};

const handlePaste = (event) => {
  const pasted = event.clipboardData.getData('text');
  if (!/^\d*\.?\d*$/.test(pasted)) {
    event.preventDefault();
  }
};

const handleSort = (column) => {
  if (props.sortConfig.column === column) {
    if (props.sortConfig.direction === 'asc') {
      props.sortConfig.direction = 'desc';
    } else if (props.sortConfig.direction === 'desc') {
      props.sortConfig.column = null;
      props.sortConfig.direction = null;
    }
  } else {
    props.sortConfig.column = column;
    props.sortConfig.direction = 'asc';
  }
};

const handleRowClick = (row, event) => {
  const rowAction = rowClickAction(row);
  if (!rowAction) return;
  if (event.target.closest('.action-btn')) return;
  if (typeof rowAction.handler === 'function') {
    rowAction.handler(row);
  }
};

const handleAction = async (action, row, event, index) => {
  event.stopPropagation();
  const rowKey = index;
  const actionKey = action.label || action.id || index;
  activeAction[`${rowKey}-${actionKey}`] = true;

  try {
    if (typeof action.handler === 'function') {
      await action.handler(row);
    }
  } finally {
    activeAction[`${rowKey}-${actionKey}`] = false;
  }
};

const resolveActions = (row) => {
  return typeof props.actions === 'function' ? props.actions(row) : props.actions;
};

const buttonActions = (row) => {
  return resolveActions(row).filter(a => !a.type || a.type === 'button');
};

const rowClickAction = (row) => {
  return resolveActions(row).find(a => a.type === 'row-click');
};

const hasButtonActions = computed(() => {
  if (typeof props.actions === 'function') {
    const firstRow = props.sortedAndFilteredRows?.[0];
    return firstRow ? props.actions(firstRow).some(a => !a.type || a.type === 'button') : false;
  }
  return Array.isArray(props.actions) && props.actions.some(a => !a.type || a.type === 'button');
});
</script>

<style scoped>
.editable-input {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 13px;
  color: #374151;
  background-color: #ffffff;
}

.editable-input:focus {
  border-color: var(--highlights);
  outline: none;
  box-shadow: 0 0 0 1px var(--highlights-background);
}

.data-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
    animation: slideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.header-row {
    background: #f8fafc;
    position: sticky;
    top: 0;
    z-index: 3;
}

.header-cell {
    padding: 0;
    text-align: left;
    border-bottom: 1px solid #e2e8f0;
    font-weight: 500;
    background: #f8fafc;
}

.actions-cell {
    min-width: 120px;
}

.actions-header {
    text-align: center;
    min-width: 120px;
}

.action-text {
    white-space: nowrap;
    font-size: 11px;
}

.actions-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px 12px;
    flex-wrap: wrap;
}

.actions-header .header-content {
    cursor: default;
    justify-content: center;
}

.header-content:hover {
    background-color: var(--highlights-background);
}

.actions-header .header-content:hover {
    background-color: transparent;
}

.header-content,
.cell-content {
    padding: 14px 20px;
}

.sort-arrow {
    color: var(--highlights);
    transition: all 0.2s ease;
}

.sort-arrow-inactive {
    opacity: 0.3;
}

.header-content:hover .sort-arrow-inactive {
    opacity: 0.6;
}

.action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 32px;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 12px;
    font-weight: 500;
    padding: 0 8px;
}

.action-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}


/* Action button variants — wine-red brand palette.
 *
 * Variants are arranged on a single semantic axis:
 *   default → primary (edit)        → success (save, solid)
 *                       \
 *                        danger (delete) — kept on a distinct red so it still
 *                                          reads as "destructive" against the
 *                                          brand-red edit/save buttons.
 *   warning is reserved for future use; tuned amber.
 */

.action-btn-default {
    background: #f3f4f6;
    color: #6b7280;
}

.action-btn-default:hover:not(:disabled) {
    background: #6b7280;
    color: white;
    transform: scale(1.05);
}

.action-btn-primary {
    background: var(--highlights-background);
    color: var(--highlights);
}

.action-btn-primary:hover:not(:disabled) {
    background: var(--highlights);
    color: white;
    transform: scale(1.05);
}

.action-btn-success {
    background: var(--highlights);
    color: #ffffff;
}

.action-btn-success:hover:not(:disabled) {
    background: #5d1216; /* slightly darker than --highlights */
    color: #ffffff;
    transform: scale(1.05);
}

.action-btn-warning {
    background: #fef3c7;
    color: #d97706;
}

.action-btn-warning:hover:not(:disabled) {
    background: #d97706;
    color: white;
    transform: scale(1.05);
}

.action-btn-danger {
    background: #fee2e2;
    color: #dc2626;
}

.action-btn-danger:hover:not(:disabled) {
    background: #dc2626;
    color: white;
    transform: scale(1.05);
}

.header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s ease;
}

.header-text {
    font-size: 13px;
    font-weight: 500;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.data-row {
    transition: all 0.2s ease;
    border-bottom: 1px solid rgba(0, 0, 0, 0.04);
    text-align: left;
}

.data-row.row-even {
    background: rgba(248, 250, 252, 0.5);
}

.data-row.row-odd {
    background: rgba(255, 255, 255, 0.3);
}

.data-row:hover.row-even,
.data-row:hover.row-odd {
    background: linear-gradient(90deg, rgba(123, 24, 28, 0.05) 0%, rgba(123, 24, 28, 0.02) 100%);
}

.data-row.row-clickable:hover.row-even,
.data-row.row-clickable:hover.row-odd {
    background: linear-gradient(90deg, rgba(123, 24, 28, 0.08) 0%, rgba(123, 24, 28, 0.04) 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(123, 24, 28, 0.15);
}

.data-cell {
    padding: 0;
    border-bottom: inherit;
}

.cell-content {
    color: #374151;
    font-weight: 500;
    line-height: 1.4;
    word-break: break-word;
}

.data-row.row-clickable {
    cursor: pointer;
}

.sort-indicator {
    display: flex;
    align-items: center;
    margin-left: 8px;
}


@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateY(12px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}
.action-btn.loading {
  background-color: rgba(123, 24, 28, 0.15);
}
.action-btn svg,
.action-btn .action-text {
  transition: opacity 0.2s ease;
}



.spinner-icon {
  animation: spinner-rotate 0.75s linear infinite;
  transform-origin: center;
}

@keyframes spinner-rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.toggle-btn {
  display: inline-flex;
  align-items: center;
  width: 40px;
  height: 22px;
  border: none;
  border-radius: 11px;
  cursor: pointer;
  padding: 3px;
  transition: background 0.2s ease;
  user-select: none;
  flex-shrink: 0;
}

.toggle-btn:active {
  opacity: 0.8;
}

.toggle-knob {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.25);
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.toggle-btn--on {
  background: var(--highlights);
  justify-content: flex-start;
}

.toggle-btn--on .toggle-knob {
  transform: translateX(18px);
}

.toggle-btn--off {
  background: #d1d5db;
  justify-content: flex-start;
}

.toggle-btn--off .toggle-knob {
  transform: translateX(0);
}

.row-badge {
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #eef2ff;
  color: #4338ca;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.2px;
  white-space: nowrap;
}

</style>